import React, { useState, useEffect } from "react";
import {
    Paper,
    Grid,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    MenuItem,
} from "@mui/material";
import { makeStyles } from 'tss-react/mui';
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon, HelpOutlineOutlined } from "@mui/icons-material";

import { toast } from "../../services/toast";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";
import SettingsCard from "../Settings/SettingsCard";
import { helpPageLabel, helpPages } from "../../helpers/helpPages";
import { invalidateHelpsCache } from "../PageHelp";


const useStyles = makeStyles()(theme => ({
	root: {
		width: '100%'
	},
    mainPaper: {
		width: '100%',
		flex: 1,
		padding: theme.spacing(1),
		background: "transparent",
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.5),
    },
	fullWidth: {
		width: '100%'
	},
	actions: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		justifyContent: "flex-end",
		marginTop: theme.spacing(2),
	},
    tableContainer: {
		width: '100%',
		height: 420,
		overflowY: "auto",
		overflowX: "auto",
		borderRadius: 12,
		background: theme.palette.background.paper,
		boxShadow: theme.palette.mode === "light"
			? "0 1px 4px rgba(0,0,0,0.08)"
			: "0 1px 4px rgba(0,0,0,0.4)",
		...theme.scrollbarStyles
    },
	tableHead: {
		"& th": {
			fontWeight: 700,
			fontSize: "0.8rem",
			whiteSpace: "nowrap",
			background: theme.palette.mode === "light" ? "#f5f6f8" : "rgba(255,255,255,0.04)",
		},
	},
}));

export function HelpManagerForm (props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const { classes } = useStyles()

    const [record, setRecord] = useState(initialValue);

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async(data) => {
        onSubmit(data)
    }

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={(values, { resetForm }) =>
                setTimeout(() => {
                    handleSubmit(values)
                    resetForm()
                }, 500)
            }
        >
            {(values) => (
                <Form className={classes.fullWidth}>
                    <Grid spacing={2} container>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field
                                as={TextField}
                                label="Título"
                                name="title"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                helperText="Nombre del artículo de ayuda"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field
                                as={TextField}
                                label="Video de YouTube"
                                name="video"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                                helperText="Pegá el enlace del video o solo su código"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field
                                as={TextField}
                                select
                                label="Página donde aparece"
                                name="page"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                helperText="En esa página se muestra el botón para ver este video"
                                slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
                            >
                                <MenuItem value="">Solo en la sección Ayuda</MenuItem>
                                {helpPages().map((p) => (
                                    <MenuItem key={p.path} value={p.path}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Field>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                            <Field
                                as={TextField}
                                label="Descripción"
                                name="description"
                                variant="outlined"
                                size="small"
                                className={classes.fullWidth}
                                helperText="Texto que se muestra bajo el título"
                            />
                        </Grid>
                    </Grid>
                    <div className={classes.actions}>
                        <ButtonWithSpinner loading={loading} onClick={() => onCancel()} variant="outlined">
                            Limpiar
                        </ButtonWithSpinner>
                        {record.id !== undefined && (
                            <ButtonWithSpinner loading={loading} onClick={() => onDelete(record)} variant="outlined" color="secondary">
                                Eliminar
                            </ButtonWithSpinner>
                        )}
                        <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
                            Guardar
                        </ButtonWithSpinner>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export function HelpsManagerGrid (props) {
    const { records, onSelect } = props
    const { classes } = useStyles()

    return (
        <Paper className={classes.tableContainer} elevation={0}>
            <Table className={classes.fullWidth} size="small" stickyHeader aria-label="a dense table">
                <TableHead className={classes.tableHead}>
                <TableRow>
                    <TableCell align="center" style={{width: '1%'}}>#</TableCell>
                    <TableCell align="left">Título</TableCell>
                    <TableCell align="left">Descripción</TableCell>
                    <TableCell align="left">Vídeo</TableCell>
                    <TableCell align="left">Página</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {records.map((row) => (
                    <TableRow key={row.id} hover>
                        <TableCell align="center" style={{width: '1%'}}>
                            <IconButton onClick={() => onSelect(row)} aria-label="editar" size="small">
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </TableCell>
                        <TableCell align="left">{row.title || '-'}</TableCell>
                        <TableCell align="left">{row.description || '-'}</TableCell>
                        <TableCell align="left">{row.video || '-'}</TableCell>
                        <TableCell align="left">{row.page ? helpPageLabel(row.page) : 'Solo en Ayuda'}</TableCell>
                    </TableRow>
                ))}
                </TableBody>
            </Table>
        </Paper>
    );
}

export default function HelpsManager () {
    const { classes } = useStyles()
    const { list, save, update, remove } = useHelps()
    
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState({
        title: '',
        description: '',
        video: '',
        page: ''
    })

    useEffect(() => {
        async function fetchData () {
            await loadHelps()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadHelps = async () => {
        setLoading(true)
        try {
            const helpList = await list()
            setRecords(helpList)
        } catch (e) {
            toast.error('No se puede cargar la lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        try {
            if (data.id !== undefined) {
                await update(data)
                invalidateHelpsCache()
            } else {
                await save(data)
                invalidateHelpsCache()
            }
            await loadHelps()
            handleCancel()
            toast.success('¡Operación realizada con éxito!')
        } catch (e) {
            toast.error('No se puede realizar la operación. Comprueba si existe un helpo con el mismo nombre o si los campos están rellenados correctamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            invalidateHelpsCache()
            await loadHelps()
            handleCancel()
            toast.success('¡Operación realizada con éxito!')
        } catch (e) {
            toast.error('No se puede realizar la operación')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({
            title: '',
            description: '',
            video: '',
            page: ''
        })
    }

    const handleSelect = (data) => {
        setRecord({
            id: data.id,
            title: data.title || '',
            description: data.description || '',
            video: data.video || '',
            page: data.page || ''
        })
    }

    return (
        <Paper className={classes.mainPaper} elevation={0}>
            <SettingsCard
                icon={HelpOutlineOutlined}
                title={i18n.t("settings.tabs.helps")}
                subtitle="Crea artículos que tus usuarios verán en la sección Ayuda. Cada artículo tiene un título, una descripción y, opcionalmente, un video de YouTube que se reproduce dentro del sistema."
            >
                <HelpManagerForm
                    initialValue={record}
                    onDelete={handleOpenDeleteDialog}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    loading={loading}
                />
            </SettingsCard>
            <HelpsManagerGrid
                records={records}
                onSelect={handleSelect}
            />
            <ConfirmationModal
                title="Eliminar el Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                ¿Realmente desea eliminar este registro?
            </ConfirmationModal>
        </Paper>
    );
}